import React from 'react';
import { CustomShipmentText, CustomTitle } from '../../../components';
import { Button, Card, Col, Flex, Row, Space, Tag, Tooltip } from 'antd';
import { AlertFilterIcon } from '@/assets/icons/icons';
import { truncateByLength } from '@/common/helper';
import { SingleShipmentDataExtended } from '@/apis/types';
import {
  deliveredDeliveryPillColors,
  neutralStatusPillColors,
} from '../statusTagColors';

interface LinkedShipmentCardProps {
  shipment: SingleShipmentDataExtended;
  filterByLinkCode: (linkCode: string) => void;
  setShipmentId: (shipmentId: string) => void;
  onLinkedShipmentClick?: (shipmentId: string) => void;
}

interface StatusStyles {
  backgroundColor: string;
  color: string;
}

/**
 * Component displaying linked shipments with filtering and navigation capabilities
 *
 * @param shipment - Shipment object containing linked shipments data
 * @param filterByLinkCode - Callback to filter shipments by link code
 * @param setShipmentId - Callback to navigate to specific shipment
 */
const LinkedShipmentCard: React.FC<LinkedShipmentCardProps> = ({
  shipment,
  filterByLinkCode,
  setShipmentId,
  onLinkedShipmentClick,
}) => {
  // for link shipment tag colors
  const getStatusStyles = (status: string): StatusStyles => {
    switch (status?.toUpperCase()) {
      case 'LOW BATTERY':
        return {
          backgroundColor: 'var(--light-warning)',
          color: 'var(--warning)',
        };
      case 'DELIVERED':
        return deliveredDeliveryPillColors;
      case 'AWAITING PICKUP':
      case 'IN-TRANSIT':
      case 'PENDING':
      case 'NOT FOUND':
        return neutralStatusPillColors;
      default:
        return { backgroundColor: '#e0e0e0', color: 'var(--text-primary)' }; // fallback style
    }
  };
  return (
    <>
      <div>
        <Row
          justify='space-between'
          align='middle'
          style={{ marginBottom: '8px', marginTop: '10px' }}
        >
          <Col>
            <CustomTitle
              text={'Linked shipments'}
              fontSize='15px'
              fontWeight={700}
              lineHeight='28px'
              color='var(--text-primary)'
              marginBottom='0'
            />
          </Col>
          <Col>
            <Button
              type='text'
              style={{
                backgroundColor: 'var(--light-gray)',
                borderRadius: '8px',
                height: '28px',
                padding: '4px 8px',
              }}
              onClick={() => {
                if (shipment?.link_code) filterByLinkCode(shipment.link_code);
              }}
            >
              <AlertFilterIcon width={12} height={12} />
              <CustomShipmentText text={'View'} fontWeight={600} />
            </Button>
          </Col>
        </Row>

        {shipment?.linked_shipments.map((linked) => (
          <Card
            key={linked.id}
            style={{
              marginBottom: '8px',
              borderRadius: '12px',
              border: '2px solid #E2E2E2',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              cursor: 'pointer',
            }}
            styles={{ body: { padding: '16px' } }}
            onClick={() => {
              setShipmentId(linked?.id);
              onLinkedShipmentClick?.(linked?.id);
            }}
          >
            <div
              // eslint-disable-next-line react/forbid-dom-props -- layout styles predate the strict rule; to move into CSS
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Flex>
                <Space>
                  {/* eslint-disable-next-line react/forbid-dom-props -- layout styles predate the strict rule; to move into CSS */}
                  <div style={{ width: '120px' }}>
                    {linked.reference_id.length > 15 ? (
                      <Tooltip title={linked.reference_id}>
                        <span>
                          <CustomTitle
                            text={truncateByLength(linked.reference_id, 12)}
                            color={'#000'}
                            fontSize='14px'
                            lineHeight='16px'
                            fontWeight='600'
                            marginBottom='0px'
                            whiteSpace='nowrap'
                          />
                        </span>
                      </Tooltip>
                    ) : (
                      <CustomTitle
                        text={truncateByLength(linked.reference_id, 12)}
                        color={'#000'}
                        fontSize='14px'
                        lineHeight='16px'
                        fontWeight='600'
                        marginBottom='0px'
                        whiteSpace='nowrap'
                      />
                    )}
                  </div>

                  <Tag
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      lineHeight: '16px',
                      borderRadius: '4px',
                      padding: '2px 10px',
                      ...getStatusStyles(linked.status.toLowerCase()),
                      border: 'none',
                    }}
                  >
                    {linked.status}
                  </Tag>
                </Space>
              </Flex>
              <Flex
                justify='end'
                style={{
                  width: '220px',
                }}
              >
                <CustomShipmentText text={linked.route} lineHeight='20px' />
              </Flex>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
};

export default LinkedShipmentCard;
